import type { ThunkAction, UnknownAction } from '@reduxjs/toolkit';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { config } from 'services/config';
import {
  PatientStudiesParams,
  StudiesListParams,
  Study,
  StudyListParams,
} from '@/models/study.ts';
import { Pagination } from '@/models';
import type { ApiStudyWithReports } from '@/models/study';
import type { Tag } from '@/models/tag';
import { buildQueryString, prepareHeaders } from '@/state/slices/index.ts';

interface StudiesListResponse {
  results: Study[];
}

export const StudiesAPI = createApi({
  reducerPath: 'StudiesAPI',
  baseQuery: fetchBaseQuery({
    baseUrl: config.BASE_URL,
    prepareHeaders,
  }),
  tagTypes: ['Studies', 'ReportPdf'],
  endpoints: (builder) => ({
    getStudies: builder.query<Study[], StudiesListParams | void>({
      query: (params) => {
        if (!params) {
          return { url: 'studies/' };
        }

        const { patientUuid } = params;

        return {
          url: 'studies/',
          params: {
            patient_uuid: patientUuid,
          },
        };
      },
      transformResponse: (response: StudiesListResponse) => response.results,
      providesTags: (result) =>
        result
          ? [
              ...result.map((study) => ({
                type: 'Studies' as const,
                id: study.uuid,
              })),
              { type: 'Studies', id: 'LIST' },
            ]
          : [{ type: 'Studies', id: 'LIST' }],
    }),

    getStudiesList: builder.query<Pagination<Study>, StudyListParams>({
      query: (filters) => {
        const queryString = buildQueryString(filters as Record<string, string>);
        return { url: `studies/${queryString}`, method: 'GET' };
      },
      providesTags: (result) =>
        result
          ? [
              ...(result.results ?? []).map((study) => ({
                type: 'Studies' as const,
                id: study.uuid,
              })),
              { type: 'Studies', id: 'LIST' },
            ]
          : [{ type: 'Studies', id: 'LIST' }],
    }),

    getStudy: builder.query<Study, { uuid: string }>({
      query: ({ uuid }) => ({ url: `studies/${uuid}/` }),
      providesTags: (_result, _error, { uuid }) => [
        { type: 'Studies', id: uuid },
      ],
    }),

    getPatientStudies: builder.query<
      ApiStudyWithReports[],
      PatientStudiesParams
    >({
      query: (params) => {
        const queryString = buildQueryString(
          params as unknown as Record<string, string>
        );
        return { url: `studies/${queryString}`, method: 'GET' };
      },
      transformResponse: (response: { results: ApiStudyWithReports[] }) =>
        response.results,
      providesTags: (result) =>
        result
          ? [
              ...result.map((study) => ({
                type: 'Studies' as const,
                id: study.uuid,
              })),
              { type: 'Studies', id: 'PATIENT_LIST' },
            ]
          : [{ type: 'Studies', id: 'PATIENT_LIST' }],
    }),

    // Deliberately its own tag rather than `Studies` — a study is invalidated
    // by things that do not change the report PDF (view tracking, assignment
    // changes), and reusing it would trigger needless regeneration.
    exportReport: builder.query<Blob, { studyUuid: string }>({
      query: ({ studyUuid }) => ({
        url: `studies/${studyUuid}/export-report/`,
        responseHandler: async (response) => response.blob(),
      }),
      providesTags: (_result, _error, { studyUuid }) => [
        { type: 'ReportPdf', id: studyUuid },
      ],
      keepUnusedDataFor: 300,
    }),
  }),
});

/**
 * The report PDF is derived from report content, so any write to the report
 * makes it stale.
 *
 * Note the cost: while the reader is open it holds a live subscription (see
 * `useReportPdfPrefetch`), so each invalidation triggers an immediate refetch
 * and therefore a server-side regeneration. That is deliberate — it is what
 * keeps the preview instant on arrival — but it means an autosave-heavy editing
 * session regenerates the PDF roughly once per debounce interval.
 */
export const invalidateReportPdf = (studyUuid: string) =>
  StudiesAPI.util.invalidateTags([
    { type: 'ReportPdf' as const, id: studyUuid },
  ]);

/**
 * A report template edit changes how every study's PDF renders, so drop all
 * cached PDFs rather than one study's.
 */
export const invalidateAllReportPdfs = () =>
  StudiesAPI.util.invalidateTags(['ReportPdf']);

/**
 * For writes that change the study record itself, not just report content.
 * Deliberately not used by `updateReport`: a report save leaves the study row
 * untouched, and the reader holds a live `getStudy` subscription that would
 * otherwise refetch on every autosave.
 */
export const invalidateStudyCaches = (studyUuid: string) =>
  StudiesAPI.util.invalidateTags([
    { type: 'Studies' as const, id: studyUuid },
    { type: 'ReportPdf' as const, id: studyUuid },
  ]);

type StudiesRootState = Parameters<
  typeof StudiesAPI.util.selectCachedArgsForQuery
>[0];
type PatchCollection = ReturnType<
  ReturnType<typeof StudiesAPI.util.updateQueryData>
>;

/**
 * Sets the tags on the study row of this report in every cached worklist,
 * without a refetch. The row's tags are its latest report's tags, so a
 * report save is all it takes to keep the lists right. Returns the patches
 * so a failed save can undo them.
 */
export const setWorklistTags =
  (
    reportUuid: string,
    tags: Tag[]
  ): ThunkAction<PatchCollection[], StudiesRootState, unknown, UnknownAction> =>
  (dispatch, getState) =>
    StudiesAPI.util
      .selectCachedArgsForQuery(getState(), 'getStudiesList')
      .map((args) =>
        dispatch(
          StudiesAPI.util.updateQueryData('getStudiesList', args, (draft) => {
            const study = draft.results?.find(
              (s) => s.reportUuid === reportUuid
            );
            // Same tags (e.g. an autosave of an untouched report): leave the
            // cache as is, so the lists don't re-render.
            const same =
              study?.tags.length === tags.length &&
              study.tags.every((tag, i) => tag.value === tags[i].value);
            if (study && !same) study.tags = tags;
          })
        )
      );

export const {
  useGetStudiesQuery,
  useLazyGetStudiesQuery,
  useGetStudiesListQuery,
  useGetPatientStudiesQuery,
  useGetStudyQuery,
  useLazyGetStudyQuery,
  useExportReportQuery,
  useLazyExportReportQuery,
} = StudiesAPI;
